/**
 * Vehicle detail hook — TanStack Query for guest-facing vehicle view.
 *
 * Fetches full vehicle data with host profile, images, and reviews.
 * Exports query key factory + prefetch helper for eager loading from card taps.
 */
import { useQuery } from '@tanstack/react-query'
import { vehicleService } from '../services/vehicle.service'
import { queryClient } from '../query-client'

const vehicleDetailKeys = {
  all: ['vehicle-detail'] as const,
  detail: (vehicleId: string) => [...vehicleDetailKeys.all, vehicleId] as const,
}

/** Prefetch vehicle detail into cache — call on card press for instant detail load */
export function prefetchVehicleDetail(vehicleId: string): void {
  void queryClient.prefetchQuery({
    queryKey: vehicleDetailKeys.detail(vehicleId),
    queryFn: () => vehicleService.getVehicleDetail(vehicleId),
    staleTime: 10 * 60 * 1000, // 10 min — vehicle data is mostly static
  })
}

export function useVehicleDetail(vehicleId: string | undefined) {
  const query = useQuery({
    queryKey: vehicleDetailKeys.detail(vehicleId ?? ''),
    queryFn: () => vehicleService.getVehicleDetail(vehicleId!),
    enabled: !!vehicleId,
    retry: false,
    staleTime: 10 * 60 * 1000, // 10 min — vehicle data is mostly static
  })

  return {
    vehicle: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
